// ClientExecutiveLens — compact header card + accordion-revealed details.
// Reads the actual weeklyLens shape from clients/{id}.json (v1):
//   - topOpportunities[]: { category, title, summary, whyItMatters, relatedTrackIDs }
//   - topThreats[]: same shape
//   - topBlindSpots[]: same shape
//   - topRecommendedActions[]: { rank, priority, action, owner, timeframe,
//                                rationale, relatedTrackIDs, relatedTrackNames }

import React, { useState } from "react";
import Accordion from "./Accordion";
import RecommendedActionCard from "./RecommendedActionCard";
import { CATEGORY_LABEL, CATEGORY_TONE, hasContent, normaliseRecommendedAction, normaliseNarrativeEntry, relatedTrackIdsOf, buildBlindSpotGroups, SEVERITY_TONE, readWhatMovedThisWeek, countExecutiveOpportunities, countExecutiveThreats, countExecutiveBlindSpots, countExecutiveRecommendedActions } from "../../../lib/clients";

const Badge = ({ tone = "muted", children }) => (
    <span className={`badge ${tone === "accent" ? "badge-accent" : tone === "positive" ? "badge-positive" : tone === "warning" ? "badge-warning" : tone === "negative" ? "badge-negative" : tone === "regulatory" ? "badge-regulatory" : tone === "ink" ? "badge-ink" : ""}`}>
        {children}
    </span>
);

const Pill = ({ label, value, tone }) => (
    <div className="flex flex-col">
        <div className="eyebrow">{label}</div>
        <div className="mt-1.5"><Badge tone={tone}>{value || "—"}</Badge></div>
    </div>
);

const summaryClamp = (text, lines = 4) => ({
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: String(lines),
    overflow: "hidden",
});

const NarrativeItem = ({ entry, onSelectTrack, trackCardsById }) => {
    if (!entry || (!hasContent(entry.title) && !hasContent(entry.summary))) return null;
    const cat = (entry.category || "").toUpperCase();
    const tone = CATEGORY_TONE[cat] || "muted";
    const label = CATEGORY_LABEL[cat] || entry.category;
    const related = relatedTrackIdsOf(entry);
    const severity = (entry.severity || "").toUpperCase();
    const severityTone = SEVERITY_TONE[severity] || null;
    return (
        <article className="card-sunken p-3.5" data-testid="exec-narrative-item">
            <header className="flex items-center gap-2 flex-wrap mb-1.5">
                {label && <Badge tone={tone}>{label}</Badge>}
                {severity && severityTone && <Badge tone={severityTone}>{severity}</Badge>}
                {hasContent(entry.title) && <span className="text-sm font-semibold text-[hsl(var(--ink))] leading-snug">{entry.title}</span>}
            </header>
            {hasContent(entry.summary) && <p className="text-xs text-[hsl(var(--ink-soft))] leading-relaxed mt-1">{entry.summary}</p>}
            {hasContent(entry.whyItMatters) && (
                <p className="text-[11px] text-[hsl(var(--ink-soft))] leading-relaxed mt-2 border-l-2 border-[hsl(var(--accent))] pl-2 italic">{entry.whyItMatters}</p>
            )}
            {hasContent(entry.recommendedAction) && (
                <p className="text-[11px] text-[hsl(var(--ink-soft))] leading-relaxed mt-2">
                    <span className="text-[hsl(var(--mute))] uppercase tracking-wider text-[10px] font-medium mr-1">Suggested step</span>
                    {entry.recommendedAction}
                </p>
            )}
            {related.length > 0 && onSelectTrack && (
                <div className="flex flex-wrap gap-1.5 mt-2.5 items-center">
                    <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium">Open signal</span>
                    {related.slice(0, 4).map((tid) => {
                        const name = trackCardsById?.[tid]?.stableTrackName || tid;
                        return (
                            <button
                                key={tid}
                                type="button"
                                onClick={() => onSelectTrack(tid)}
                                data-testid={`exec-related-${tid}`}
                                className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[hsl(var(--surface-raised))] border border-[hsl(var(--line))] text-[hsl(var(--ink-soft))] hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent-ink))]"
                                title={tid}
                            >
                                {name}
                            </button>
                        );
                    })}
                </div>
            )}
        </article>
    );
};

const WhatMovedThisWeek = ({ items, onSelectTrack, trackCardsById }) => {
    const [showAll, setShowAll] = useState(false);
    if (!Array.isArray(items) || items.length === 0) return null;
    const visible = showAll ? items : items.slice(0, 3);

    return (
        <div data-testid="what-moved-this-week">
            <Accordion
                label="What moved this week"
                count={items.length}
                defaultOpen={false}
                testId="what-moved-this-week-accordion"
            >
                <p className="text-[11px] text-[hsl(var(--mute))] leading-relaxed -mt-1 mb-3">Material weekly shifts that matter most for you.</p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    {visible.map((entry, i) => {
                        const related = relatedTrackIdsOf(entry);
                        return (
                            <li key={`wm-${entry.title || ""}-${i}`} className="card-sunken p-2.5" data-testid="what-moved-item">
                                {hasContent(entry.title) && (
                                    <div className="text-sm font-semibold text-[hsl(var(--ink))] leading-snug">{entry.title}</div>
                                )}
                                {hasContent(entry.summary) && (
                                    <p className="text-xs text-[hsl(var(--ink-soft))] leading-relaxed mt-1">{entry.summary}</p>
                                )}
                                {hasContent(entry.clientRelevance) && (
                                    <p className="text-[11px] text-[hsl(var(--ink-soft))] leading-relaxed mt-1.5 border-l-2 border-[hsl(var(--accent))] pl-2 italic">
                                        <span className="text-[hsl(var(--mute))] uppercase tracking-wider text-[10px] font-medium mr-1.5 not-italic">Why this matters</span>
                                        {entry.clientRelevance}
                                    </p>
                                )}
                                {related.length > 0 && onSelectTrack && (
                                    <div className="flex flex-wrap gap-1.5 mt-2 items-center">
                                        <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium">Open signal</span>
                                        {related.slice(0, 4).map((tid) => {
                                            const name = trackCardsById?.[tid]?.stableTrackName || tid;
                                            return (
                                                <button
                                                    key={tid}
                                                    type="button"
                                                    onClick={() => onSelectTrack(tid)}
                                                    data-testid={`what-moved-track-${tid}`}
                                                    className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[hsl(var(--surface-raised))] border border-[hsl(var(--line))] text-[hsl(var(--ink-soft))] hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent-ink))]"
                                                    title={tid}
                                                >
                                                    {name}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
                {items.length > 3 && (
                    <button
                        type="button"
                        onClick={() => setShowAll((v) => !v)}
                        data-testid="what-moved-toggle"
                        className="text-[11px] font-medium text-[hsl(var(--accent-ink))] hover:underline mt-2"
                    >
                        {showAll ? "Show top 3" : `Show all ${items.length}`}
                    </button>
                )}
            </Accordion>
        </div>
    );
};

const ClientExecutiveLens = ({ weeklyLens, onSelectTrack, trackCardsById }) => {
    const [expandSummary, setExpandSummary] = useState(false);

    if (!weeklyLens) {
        return (
            <section className="card-flat p-6" data-testid="executive-client-lens-empty">
                <div className="eyebrow">Executive client read</div>
                <p className="text-sm text-[hsl(var(--mute))] mt-2 leading-relaxed">No weekly client lens available for this week.</p>
            </section>
        );
    }

    // STRICT RENDER / COUNT 1:1 — every section renders exactly one upstream
    // array, and the section header count equals the number of cards that
    // section actually renders. We normalise each entry (PascalCase ↔
    // lowerCamel) and drop ones that would render to nothing (no title AND
    // no summary), so the header count and the visible card count cannot
    // disagree. No cross-array fallback, no Type/category filtering. The
    // detailed-diagnostics blind spots are surfaced in their OWN section
    // (with its own count) further below.
    const renderableEntries = (arr) => {
        if (!Array.isArray(arr)) return [];
        const out = [];
        for (const raw of arr) {
            const e = normaliseNarrativeEntry(raw);
            if (!e || typeof e !== "object") continue;
            if (!hasContent(e.title) && !hasContent(e.summary)) continue;
            out.push(e);
        }
        return out;
    };

    const opps = renderableEntries(weeklyLens.topOpportunities);
    const threats = renderableEntries(weeklyLens.topThreats);
    const topBlind = renderableEntries(weeklyLens.topBlindSpots);

    const recs = (Array.isArray(weeklyLens.topRecommendedActions) ? weeklyLens.topRecommendedActions : [])
        .map(normaliseRecommendedAction)
        .filter((a) => a && hasContent(a.actionTitle));

    // Detailed blind-spot diagnostics — fed by `structuredBlindSpots`,
    // `profileLevelBlindSpots`, and `signalLinkedBlindSpots`. NEVER mixed
    // with `topBlindSpots`. Legacy `topBlindSpots`-only JSONs return empty
    // groups here (we pass `legacyTopBlindSpots: null`), so the diagnostics
    // section stays hidden and we don't double-show the same entries.
    const blindGroups = buildBlindSpotGroups({
        profileLevelBlindSpots: weeklyLens.profileLevelBlindSpots,
        signalLinkedBlindSpots: weeklyLens.signalLinkedBlindSpots,
        structuredBlindSpots: weeklyLens.structuredBlindSpots,
        legacyTopBlindSpots: null,
    });
    const profileBlind = blindGroups.profile.map(normaliseNarrativeEntry).filter((e) => hasContent(e?.title) || hasContent(e?.summary));
    const signalBlind = blindGroups.signalLinked.map(normaliseNarrativeEntry).filter((e) => hasContent(e?.title) || hasContent(e?.summary));
    const detailedBlindCount = profileBlind.length + signalBlind.length;

    // Executive topline counts — derived from the same source as the
    // per-section renders so topline === each section header === card count.
    const oppCount = opps.length;
    const threatCount = threats.length;
    const blindCount = topBlind.length;
    const recsCount = recs.length;

    const whatMoved = readWhatMovedThisWeek(weeklyLens);

    // Executive narrative — pick the cleanest client-facing prose available.
    // Order of preference:
    //   1. lowerCamel `executiveSummary` (newer feeds)
    //   2. PascalCase `ExecutiveSummary`
    //   3. PascalCase `ExecutiveClientSummary`
    //   4. lowerCamel `executiveClientSummary` (current LBBW shape)
    // We deliberately DO NOT fall back to `clientPositionSummary` /
    // `clientPositioningSummary` — those carry raw enum-style positioning
    // text ("Overall position: IN_POSITION...") that isn't client-facing.
    const executiveNarrative = (() => {
        for (const key of ["executiveSummary", "ExecutiveSummary", "ExecutiveClientSummary", "executiveClientSummary"]) {
            const v = weeklyLens?.[key];
            if (hasContent(v)) return v;
        }
        return null;
    })();

    const relevanceTone = (() => {
        const u = (weeklyLens.overallClientRelevance || "").toUpperCase();
        if (u === "VERY_HIGH" || u === "HIGH") return "ink";
        if (u === "MEDIUM") return "warning";
        return "muted";
    })();
    const urgencyTone = (() => {
        const u = (weeklyLens.overallUrgency || "").toUpperCase();
        if (u === "VERY_HIGH" || u === "HIGH") return "negative";
        if (u === "MEDIUM") return "warning";
        return "muted";
    })();

    return (
        <section className="card-flat p-6" data-testid="executive-client-lens">
            <header className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                    <div className="eyebrow">Executive client read</div>
                    <h2 className="display text-2xl mt-1 leading-snug">Week {weeklyLens.yearWeek}{weeklyLens.startDate ? ` · ${weeklyLens.startDate} → ${weeklyLens.endDate}` : ""}</h2>
                </div>
                <div className="flex items-center gap-6">
                    <Pill label="Client relevance" value={weeklyLens.overallClientRelevance} tone={relevanceTone} />
                    <Pill label="Urgency" value={weeklyLens.overallUrgency} tone={urgencyTone} />
                </div>
            </header>

            {hasContent(executiveNarrative) && (
                <div className="mt-3" data-testid="executive-client-summary">
                    <p
                        className="text-sm text-[hsl(var(--ink))] leading-relaxed whitespace-pre-line"
                        style={expandSummary ? undefined : summaryClamp(executiveNarrative, 4)}
                    >
                        {executiveNarrative}
                    </p>
                    <button
                        type="button"
                        onClick={() => setExpandSummary((e) => !e)}
                        className="text-[11px] font-medium text-[hsl(var(--accent-ink))] hover:underline mt-1.5"
                        data-testid="executive-summary-toggle"
                    >
                        {expandSummary ? "Show less" : "Read full summary"}
                    </button>
                </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[hsl(var(--mute))]" data-testid="exec-counts">
                <span data-testid="exec-count-opportunities">{oppCount} opportunities</span>
                <span aria-hidden>·</span>
                <span data-testid="exec-count-threats">{threatCount} threats</span>
                <span aria-hidden>·</span>
                <span data-testid="exec-count-blind-spots">{blindCount} visibility {blindCount === 1 ? "gap" : "gaps"}</span>
                <span aria-hidden>·</span>
                <span data-testid="exec-count-recommended-actions">{recsCount} recommended actions</span>
            </div>

            <div className="mt-3">
                {whatMoved.length > 0 && (
                    <WhatMovedThisWeek items={whatMoved} onSelectTrack={onSelectTrack} trackCardsById={trackCardsById} />
                )}
                {opps.length > 0 && (
                    <Accordion label="Top opportunities" count={opps.length} testId="acc-opportunities">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1" data-testid="acc-opportunities-list">
                            {opps.map((e, i) => <NarrativeItem key={`opp-${e?.title || ""}-${(e?.relatedTrackIds || []).join(",")}-${i}`} entry={e} onSelectTrack={onSelectTrack} trackCardsById={trackCardsById} />)}
                        </div>
                    </Accordion>
                )}
                {threats.length > 0 && (
                    <Accordion label="Top threats" count={threats.length} testId="acc-threats">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1" data-testid="acc-threats-list">
                            {threats.map((e, i) => <NarrativeItem key={`thr-${e?.title || ""}-${(e?.relatedTrackIds || []).join(",")}-${i}`} entry={e} onSelectTrack={onSelectTrack} trackCardsById={trackCardsById} />)}
                        </div>
                    </Accordion>
                )}
                {topBlind.length > 0 && (
                    <Accordion label="Key visibility gaps" count={topBlind.length} testId="acc-blind-spots">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1" data-testid="acc-blind-spots-list">
                            {topBlind.map((e, i) => <NarrativeItem key={`tb-${e?.title || ""}-${i}`} entry={e} onSelectTrack={onSelectTrack} trackCardsById={trackCardsById} />)}
                        </div>
                    </Accordion>
                )}
                {recs.length > 0 && (
                    <Accordion label="Where to act first" count={recs.length} testId="acc-recommended-actions">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1" data-testid="acc-recommended-actions-list">
                            {recs.map((a, i) => <RecommendedActionCard key={a.actionId || i} action={a} onSelectTrack={onSelectTrack} trackCardsById={trackCardsById} />)}
                        </div>
                    </Accordion>
                )}
                {detailedBlindCount > 0 && (
                    <Accordion
                        label="Full visibility-gap inventory"
                        count={detailedBlindCount}
                        testId="acc-blind-spots-detailed"
                        defaultOpen={false}
                    >
                        <p className="text-[11px] text-[hsl(var(--mute))] leading-relaxed mt-1 mb-3">Full inventory — separate from the executive Key visibility gaps above. May overlap.</p>
                        {profileBlind.length > 0 && (
                            <div className="mb-3" data-testid="blind-spots-profile">
                                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium mb-1.5">Profile-level · capability or governance gaps ({profileBlind.length})</div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {profileBlind.map((e, i) => <NarrativeItem key={`pb-${i}`} entry={e} onSelectTrack={onSelectTrack} trackCardsById={trackCardsById} />)}
                                </div>
                            </div>
                        )}
                        {signalBlind.length > 0 && (
                            <div data-testid="blind-spots-signal-linked">
                                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium mb-1.5">Signal-linked · gaps tied to a specific market signal ({signalBlind.length})</div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {signalBlind.map((e, i) => <NarrativeItem key={`sb-${i}`} entry={e} onSelectTrack={onSelectTrack} trackCardsById={trackCardsById} />)}
                                </div>
                            </div>
                        )}
                    </Accordion>
                )}
            </div>
        </section>
    );
};

export default ClientExecutiveLens;
